import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  clearCarts,
  getCartItemsFromResponse,
  getCarts,
  syncCartItems,
} from "../services/productService";
import { getLocalUserId } from "../utils/getLocalUserId";
import { getToken } from "../utils/getToken";
import { openCartDrawer } from "../utils/cartDrawerEvents";
import { getProfileFromResponse, getProfileUserId, getUserDetails } from "../services/Users";
import { buildCartEventPayload, META_PIXEL_EVENTS, trackMetaEvent } from "../utils/metaPixel";

export const getCartQueryKey = (user_id) => ["get-cart", { user_id }];

function getStoredCartItems() {
  try {
    return Object.values(JSON.parse(localStorage.getItem("carts") || "{}"));
  } catch (error) {
    return [];
  }
}

function keyCartItems(items = []) {
  return items.reduce((acc, item) => {
    if (item?.product_id) {
      acc[String(item.product_id)] = item;
    }

    return acc;
  }, {});
}

function saveStoredCartItems(items = []) {
  localStorage.setItem("carts", JSON.stringify(keyCartItems(items)));
}

function getNumericValue(...values) {
  for (const value of values) {
    const numericValue = Number(value);

    if (Number.isFinite(numericValue)) {
      return numericValue;
    }
  }

  return 0;
}

export function createCartItem(product, quantity = 1, variant = null) {
  const product_id =
    variant?.product_id ||
    variant?.productId ||
    product?.product_id ||
    product?.id;

  if (!product_id) {
    return null;
  }

  const price = getNumericValue(
    variant?.price,
    variant?.product_price,
    product?.product_price,
    variant?.product_sell_price,
    product?.price,
    product?.product_sell_price,
  );
  const originalPrice = getNumericValue(
    variant?.originalPrice,
    variant?.original_price,
    variant?.product_sell_price,
    product?.originalPrice,
    product?.original_price,
    product?.product_sell_price
  );

  return {
    id: product_id,
    product_id,
    product_name:
      product?.product_name ||
      product?.title ||
      product?.name ||
      "Kuku Product",
    thumbnail_image:
      product?.thumbnail_image ||
      product?.img ||
      product?.image ||
      product?.images?.[0] ||
      "",
    quantity: Math.max(1, Number(quantity) || 1),
    price,
    originalPrice,
    discount: getNumericValue(
      variant?.discount,
      product?.discount,
      product?.product_discount
    ),
    weight:
      variant?.label ||
      variant?.weight ||
      variant?.item_weight ||
      product?.weight ||
      product?.item_weight ||
      "",
  };
}

function buildQueryData(items = [], previousData) {
  return {
    ...(previousData || {}),
    data: {
      ...(previousData?.data || {}),
      data: keyCartItems(items),
    },
  };
}

function matchesCartItem(item, itemId) {
  return [item?.product_id, item?.cart_id, item?.id].some(
    (value) => value !== undefined && String(value) === String(itemId)
  );
}

export function useSyncedCart(options = {}) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState(() => getToken());

  useEffect(() => {
    const refreshToken = () => {
      const nextToken = getToken();
      setToken(nextToken);
      queryClient.invalidateQueries({ queryKey: ["auth-user-details"] });
      queryClient.invalidateQueries({ queryKey: ["get-cart"] });
    };

    window.addEventListener("login-modal-authenticated", refreshToken);
    window.addEventListener("auth-state-changed", refreshToken);
    window.addEventListener("storage", refreshToken);

    return () => {
      window.removeEventListener("login-modal-authenticated", refreshToken);
      window.removeEventListener("auth-state-changed", refreshToken);
      window.removeEventListener("storage", refreshToken);
    };
  }, [queryClient]);

  const profileQuery = useQuery({
    queryKey: ["auth-user-details", token],
    queryFn: getUserDetails,
    enabled: Boolean(token),
    retry: false,
  });
  const profileUserId = getProfileUserId(getProfileFromResponse(profileQuery.data));
  const storedUserId = localStorage.getItem("user_id");
  const user_id = token ? profileUserId || storedUserId : getLocalUserId();
  const queryKey = getCartQueryKey(user_id);
  const enabled = options.enabled !== false && Boolean(token && user_id);

  useEffect(() => {
    if (token && profileUserId) {
      localStorage.setItem("user_id", String(profileUserId));
    }
  }, [profileUserId, token]);

  const cartQuery = useQuery({
    queryKey,
    queryFn: getCarts,
    enabled,
  });

  const queryItems = getCartItemsFromResponse(cartQuery.data);
  const items = queryItems.length > 0 || enabled ? queryItems : getStoredCartItems();

  const getFreshItems = async () => {
    if (!enabled) {
      return getStoredCartItems();
    }

    const cachedData = queryClient.getQueryData(queryKey);
    const cachedItems = getCartItemsFromResponse(cachedData);

    if (cachedItems.length > 0) {
      return cachedItems;
    }

    const freshData = await queryClient.fetchQuery({
      queryKey,
      queryFn: getCarts,
    });

    return getCartItemsFromResponse(freshData);
  };

  const setCartCache = (items) => {
    queryClient.setQueriesData({ queryKey: ["get-cart"] }, (oldData) =>
      buildQueryData(items, oldData)
    );
    queryClient.setQueryData(queryKey, (oldData) =>
      buildQueryData(items, oldData)
    );
  };

  const syncItems = async (nextItems) => {
    const cleanItems = nextItems.filter(
      (item) => item?.product_id && Number(item.quantity) > 0
    );

    await queryClient.cancelQueries({ queryKey: ["get-cart"] });
    saveStoredCartItems(cleanItems);
    setCartCache(cleanItems);

    if (enabled) {
      try {
        await syncCartItems({ user_id, items: cleanItems });
        setCartCache(cleanItems);
      } catch (error) {
        queryClient.invalidateQueries({ queryKey: ["get-cart"] });
        throw error;
      }
    }

    return cleanItems;
  };

  const clearItems = async () => {
    await queryClient.cancelQueries({ queryKey: ["get-cart"] });
    saveStoredCartItems([]);
    setCartCache([]);

    if (enabled) {
      try {
        await clearCarts({ user_id });
        setCartCache([]);
      } catch (error) {
        queryClient.invalidateQueries({ queryKey: ["get-cart"] });
        throw error;
      }
    }

    return [];
  };

  const addProduct = async (product, quantity = 1, variant = null) => {
    const itemToAdd = createCartItem(product, quantity, variant);

    if (!itemToAdd) {
      return [];
    }

    const currentItems = await getFreshItems();
    const existingItem = currentItems.find(
      (item) => String(item.product_id) === String(itemToAdd.product_id)
    );
    const nextItems = existingItem
      ? currentItems.map((item) =>
          String(item.product_id) === String(itemToAdd.product_id)
            ? {
                ...item,
                ...itemToAdd,
                quantity: Number(item.quantity || 0) + itemToAdd.quantity,
              }
            : item
        )
      : [...currentItems, itemToAdd];

    const syncedItems = await syncItems(nextItems);
    trackMetaEvent(
      META_PIXEL_EVENTS.ADD_TO_CART,
      buildCartEventPayload([itemToAdd])
    );
    openCartDrawer();

    return syncedItems;
  };

  const updateQuantity = async (product_id, quantity) => {
    const currentItems = await getFreshItems();
    const nextItems = currentItems
      .map((item) =>
        matchesCartItem(item, product_id)
          ? { ...item, quantity: Number(quantity) }
          : item
      )
      .filter((item) => Number(item.quantity) > 0);

    return nextItems.length === 0 ? clearItems() : syncItems(nextItems);
  };

  const removeProduct = async (itemId) => {
    const currentItems = await getFreshItems();
    const nextItems = currentItems.filter((item) => !matchesCartItem(item, itemId));

    return nextItems.length === 0 ? clearItems() : syncItems(nextItems);
  };

  const totalAmount = items.reduce(
    (total, item) => total + Number(item.price || 0) * Number(item.quantity || 0),
    0
  );
  const cartCount = items.reduce(
    (total, item) => total + Number(item.quantity || 0),
    0
  );

  return {
    ...cartQuery,
    addProduct,
    cartCount,
    clearItems,
    items,
    refetchCart: cartQuery.refetch,
    removeProduct,
    syncItems,
    totalAmount,
    updateQuantity,
  };
}
